import Link from 'next/link';
import { useMemo } from 'react';
import { lamportsToToken } from '@/lib/utils';
import { solNetwork } from '@/lib/config';

export default function UserBidsTable({ project, bids = [] }) {
  const activeBids = useMemo(() => {
    if (!bids?.filter) {
      return [];
    }
    return (bids || []).filter((b) => ['active', 'refunding'].includes(b.status));
  }, [bids]);

  const refundedBids = useMemo(() => {
    if (!bids?.filter) {
      return [];
    }
    return (bids || []).filter((b) => b.status === 'outbid');
  }, [bids]);

  const numActiveBids = useMemo(
    () => (activeBids || []).reduce((acc, { qty }) => acc + Number(qty), 0),
    [activeBids]
  );
  const numRefundedBids = useMemo(
    () => (refundedBids || []).reduce((acc, { qty }) => acc + Number(qty), 0),
    [refundedBids]
  );

  const isDevnet = solNetwork === 'devnet';
  const [{ project_name: projectName }] = bids;

  return (
    <div className="w-full collapse collapse-arrow border rounded-box">
      <input type="checkbox" className="border-warning" />
      <div className="collapse-title w-full font-light">
        <div className="flex w-full justify-between items-center">
          <Link
            href={`/projects/${project}`}
            className="text-xl font-bold cursor-pointer hover:underline z-10"
          >
            {projectName}
          </Link>
          <div className="flex flex-col w-[140px] md:w-[210px]">
            <p className="flex font-mono">
              {activeBids?.length ? '🟢' : '⚫️'} {numActiveBids} Active
              <span className="hidden md:flex">&nbsp;bids</span>
            </p>
            <p className="flex font-mono">
              {refundedBids?.length ? '🔵' : '⚫️'} {numRefundedBids} Refunded
              <span className="hidden md:flex">&nbsp;bids</span>
            </p>
          </div>
        </div>
      </div>

      <div className="collapse-content">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Status</th>
                <th>Bid</th>
                <th>Qty</th>
                <th>Bid Tx</th>
                <th>Refund Tx</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {(bids || []).map(({ lamports, token, decimals, qty, status, bid_tx, refund_tx }) => (
                <tr key={`bid-row-${bid_tx}-${refund_tx || `${lamports}-${qty}-${status}`}`}>
                  <td>
                    {status === 'active' ? (
                      <div className="tooltip tooltip-right" data-tip="Active">
                        🟢
                      </div>
                    ) : status === 'refunding' ? (
                      <div className="tooltip tooltip-right" data-tip="Refund pending">
                        🟡
                      </div>
                    ) : (
                      <div className="tooltip tooltip-right" data-tip="Refunded">
                        🔵
                      </div>
                    )}
                  </td>
                  <td>
                    {lamportsToToken(decimals, lamports)} {token}
                  </td>
                  <td>{qty}</td>
                  <td>
                    {bid_tx && (
                      <Link
                        className="text-accent hover:underline font-mono"
                        href={`https://solscan.io/tx/${bid_tx}${isDevnet ? '?cluster=devnet' : ''}`}
                        target="_blank"
                      >
                        {bid_tx.substr(0, 8)}...{bid_tx.substr(bid_tx.length - 8, bid_tx.length)}
                      </Link>
                    )}
                  </td>
                  <td>
                    {refund_tx && (
                      <Link
                        className="text-accent hover:underline font-mono"
                        href={`https://solscan.io/tx/${refund_tx}${
                          isDevnet ? '?cluster=devnet' : ''
                        }`}
                        target="_blank"
                      >
                        {refund_tx.substr(0, 8)}...
                        {refund_tx.substr(refund_tx.length - 8, refund_tx.length)}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
